import * as Fathom from "fathom-client";
import { initDataFast } from "datafast";
import {
  DEMO_CALENDLY_URL,
  MARKETING_DATAFAST_WEBSITE_ID,
  MARKETING_FACEBOOK_PIXEL_ID,
  MARKETING_FATHOM_SITE_ID,
  MARKETING_PATHS
} from "./config";

const FACEBOOK_PIXEL_SCRIPT_ID = "tribe-facebook-pixel";

export const MARKETING_EVENTS = {
  HOME_VIEWED: "Home Viewed",
  PRO_VIEWED: "Pro Viewed",
  BOOK_DEMO_CLICKED: "Book a Demo Clicked",
  ADMIN_DASHBOARD_CLICKED: "Dashboard Clicked by Admin",
  CALENDLY_PROFILE_VIEWED: "Calendly Profile Page Viewed",
  CALENDLY_EVENT_VIEWED: "Calendly Event Type Viewed",
  CALENDLY_TIME_SELECTED: "Calendly Date and Time Selected",
  CALENDLY_SCHEDULED: "Calendly Event Scheduled"
};

const ROUTE_VIEW_EVENTS = {
  [MARKETING_PATHS.HOME]: MARKETING_EVENTS.HOME_VIEWED,
  [MARKETING_PATHS.PRO]: MARKETING_EVENTS.PRO_VIEWED
};

function createFacebookPixelQueue() {
  const fbq = function facebookPixelQueue() {
    if (fbq.callMethod) fbq.callMethod.apply(fbq, arguments);
    else fbq.queue.push(arguments);
  };
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];
  return fbq;
}

export function initializeFacebookPixel(pixelId = MARKETING_FACEBOOK_PIXEL_ID) {
  if (!pixelId || typeof window === "undefined" || typeof document === "undefined") return;

  if (!window.fbq) {
    window.fbq = createFacebookPixelQueue();
    window._fbq = window.fbq;
  }

  if (!document.getElementById(FACEBOOK_PIXEL_SCRIPT_ID)) {
    const script = document.createElement("script");
    script.id = FACEBOOK_PIXEL_SCRIPT_ID;
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(script);
  }

  window.fbq.__tribeInitializedPixelIds ||= [];
  if (!window.fbq.__tribeInitializedPixelIds.includes(pixelId)) {
    window.fbq("init", pixelId);
    window.fbq.__tribeInitializedPixelIds.push(pixelId);
  }
}

export function initializeDataFast(websiteId = MARKETING_DATAFAST_WEBSITE_ID) {
  if (!websiteId || typeof window === "undefined") return;
  if (window.__tribeLoadedDataFastWebsiteId === websiteId) return;
  window.__tribeLoadedDataFastWebsiteId = websiteId;
  // The SDK's autoCapturePageviews stays off on purpose. It monkey-patches
  // history.pushState/replaceState globally with no public teardown, and marketing → product
  // (e.g. /dashboard) is a client-side surface switch, not a page load, so those hooks would
  // keep recording product routes under the marketing property after MarketingAnalytics
  // unmounts. The client is retained instead and trackMarketingRoute sends pageviews only
  // while the marketing surface is mounted.
  window.__tribeDataFastClient = initDataFast({ websiteId }).catch(() => {
    delete window.__tribeLoadedDataFastWebsiteId;
    delete window.__tribeDataFastClient;
    return null;
  });
}

export async function trackDataFastPageview() {
  if (typeof window === "undefined" || !window.__tribeDataFastClient) return;
  const client = await window.__tribeDataFastClient;
  // No explicit path: the SDK reads window.location.href, which keeps UTMs and ad click IDs.
  if (client?.trackPageview) await client.trackPageview();
}

export function initializeMarketingAnalytics(fathomSiteId) {
  const resolvedFathomSiteId = fathomSiteId || MARKETING_FATHOM_SITE_ID;
  if (
    resolvedFathomSiteId &&
    typeof window !== "undefined" &&
    window.__tribeLoadedFathomSiteId !== resolvedFathomSiteId
  ) {
    window.__tribeLoadedFathomSiteId = resolvedFathomSiteId;
    Fathom.load(resolvedFathomSiteId, { spa: "history" });
  }
  initializeFacebookPixel();
  initializeDataFast();
}

export function trackMarketingEvent(eventName) {
  if (!eventName) return;
  Fathom.trackEvent(eventName);
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    window.fbq("trackCustom", eventName);
  }
}

export function trackMarketingRoute(pathname) {
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    window.fbq("track", "PageView");
  }
  trackMarketingEvent(ROUTE_VIEW_EVENTS[pathname]);
  void trackDataFastPageview();
}

export function getMarketingClickEvent(target, { isAdmin = false } = {}) {
  const anchor = target?.closest?.("a[href]");
  if (!anchor || typeof window === "undefined") return null;

  let destination;
  try {
    destination = new URL(anchor.href, window.location.origin);
  } catch (_error) {
    return null;
  }

  const isBookDemoPath =
    destination.origin === window.location.origin &&
    destination.pathname === MARKETING_PATHS.BOOK_DEMO;
  const isCalendlyDemo = destination.href.replace(/\/$/, "") === DEMO_CALENDLY_URL;
  if (isBookDemoPath || isCalendlyDemo) return MARKETING_EVENTS.BOOK_DEMO_CLICKED;

  const isDashboard =
    destination.origin === window.location.origin && destination.pathname === "/dashboard";
  if (isAdmin && isDashboard) return MARKETING_EVENTS.ADMIN_DASHBOARD_CLICKED;

  return null;
}

export function trackMarketingClick(event, options) {
  trackMarketingEvent(getMarketingClickEvent(event.target, options));
}

export { FACEBOOK_PIXEL_SCRIPT_ID };
